import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Btn, Field, Logo, Modal } from "@/components/rp";

const Forgot = ({ open, onClose, initial }) => {
  const [email, setEmail] = useState(initial);
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { setDone((await api.post("/auth/forgot-password", { email })).data.message); } catch (err) { setDone(errMsg(err)); }
    setLoading(false);
  };
  return (
    <Modal open={open} onOpenChange={(o) => !o && (setDone(""), onClose())} title="Password dimenticata?" description="Ti mandiamo un link valido 1 ora per sceglierne una nuova." testid="forgot-dialog">
      {done ? <p className="text-sm" data-testid="forgot-done">{done}</p> : (
        <form onSubmit={send} className="space-y-4">
          <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rp-field" data-testid="forgot-email-input" /></Field>
          <Btn type="submit" loading={loading} className="w-full" data-testid="forgot-submit">Inviami il link</Btn>
        </form>
      )}
    </Modal>
  );
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/cantiere"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/cantiere");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid min-[900px]:grid-cols-2 bg-white">
      <div className="relative hidden min-[900px]:block">
        <img src="/login.jpg" alt="Ristrutturazione RP Home Torino" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/30" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Logo white className="h-10 self-start" />
          <div className="max-w-md">
            <p className="text-[11px] uppercase tracking-[0.16em] text-white/70 font-semibold mb-3">Ristrutturazioni chiavi in mano · Torino</p>
            <p className="text-3xl lg:text-4xl font-medium tracking-tight leading-tight">Segui il tuo cantiere, passo dopo passo.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="relative min-[900px]:hidden -mx-6 sm:-mx-12 -mt-12 mb-10 h-56 overflow-hidden">
          <img src="/login.jpg" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/35 flex items-end p-6"><Logo white className="h-8" /></div>
        </div>
        <div className="w-full max-w-[420px] mx-auto rp-fade">
          <p className="rp-label mb-3">Area clienti</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Bentornato</h1>
          <p className="text-[#757575] mt-3">Accedi per vedere avanzamento, foto, documenti e messaggi del tuo cantiere.</p>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <Field label="Email">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rp-field" placeholder="nome@email.it" data-testid="login-email-input" autoComplete="email" />
            </Field>
            <Field label="Password">
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rp-field" placeholder="••••••••" data-testid="login-password-input" autoComplete="current-password" />
            </Field>
            {error && <p className="text-sm text-[#DC3545]" data-testid="login-error">{error}</p>}
            <Btn type="submit" loading={loading} className="w-full h-12" data-testid="login-submit-button">Accedi</Btn>
            <button type="button" onClick={() => setForgot(true)} className="block mx-auto text-sm text-[#757575] underline hover:text-black" data-testid="login-forgot-link">Password dimenticata?</button>
          </form>
          {forgot && <Forgot open={forgot} onClose={() => setForgot(false)} initial={email} />}
          <Link to="/richiesta-preventivo" data-testid="login-lead-link" className="group mt-10 block rp-card p-5 hover:bg-[#E6DDCE]/50 transition-colors">
            <p className="text-sm text-[#757575]">Non sei ancora cliente?</p>
            <p className="font-medium mt-1 flex items-center gap-2">Richiedi un sopralluogo e un preventivo gratuito <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></p>
          </Link>
        </div>
      </div>
    </div>
  );
}
